import React from 'react';

import PropTypes from 'prop-types';
import { HiOutlineInformationCircle } from 'react-icons/hi';
import styled from 'styled-components';

import { getMemberCreditPriceThreshold } from '~/shared/constants/productGrid';
import useByoConfigForProduct from '~/shared/utils/useByoConfigForProduct';
import useEMPPricing from '~/shared/utils/useEMPPricing';
import { useMembership } from '~/techstyle-shared/react-accounts';
import { Tooltip, mobile } from '~/techstyle-shared/react-components';
import { FormattedMessage } from '~/techstyle-shared/react-intl';
import { useDomain } from '~/techstyle-shared/redux-core';

const DynamicFormattedMessage = FormattedMessage;

const TooltipAnchor = styled(Tooltip.Anchor)`
  display: inline-block;
`;

const TooltipMessage = styled(Tooltip.Content)`
  display: flex;
  width: 250px;
  padding: 12px;
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;
  position: absolute;
  right: -230px;
  bottom: -68px;
  border-radius: 4px;
  background: ${({ theme }) => theme.colors.gr2TooltipBackground};
  box-shadow: 0.5px 1px 10px 0
    ${({ theme }) => theme.colors.gr2TooltipBoxShadow};
  backdrop-filter: blur(5px);
  color: ${({ theme }) => theme.colors.gr2TooltipMessage};
  font-family: inherit;
  font-size: 12px;
  font-style: normal;
  font-weight: 600;
  line-height: ${16 / 12};
  z-index: 10;
`;

const MemberCreditSection = styled.span`
  font-family: ${({ theme }) => theme.fontStacks.standardGtPressuraFamily};
  display: flex;
`;

const TooltipIcon = styled(HiOutlineInformationCircle)`
  width: 18px;
  height: 18px;
  flex-shrink: 0;
  aspect-ratio: 1/1;
`;

const TooltipInfoIcon = styled.span`
  padding-left: 1px;
`;

const TokenOnlyText = styled.div`
  color: ${({ theme }) => theme.colors.gr2ProductCardPricingStandard};
  font-family: inherit;
  font-size: 20px;
  ${mobile`
    font-size: 18px;
  `}
  font-style: normal;
  font-weight: 600;
  line-height: ${24 / 20};
`;

const DividerAndCredit = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;
  align-self: stretch;
`;

const Divider = styled.div`
  height: 1px;
  align-self: stretch;
  border-radius: 12px;
  background: ${({ theme }) => theme.colors.gr2ProductCardPricingDivider};
  transform: scaleY(0.5);
`;

const SmallText = styled.span`
  color: ${({ theme }) => theme.colors.gr2ProductCardPricingStandard};
  font-family: inherit;
  font-size: 14px;
  font-style: normal;
  font-weight: 350;
  line-height: ${16 / 14};
`;

const FreeTrialMemberCreditTooltip = () => {
  return (
    <Tooltip xOffset={140} yOffset={-80}>
      <TooltipAnchor>
        <TooltipInfoIcon>
          <TooltipIcon />
        </TooltipInfoIcon>
      </TooltipAnchor>
      <TooltipMessage>
        <FormattedMessage
          id="site_product_page.free-trial-member-credit-tooltip"
          defaultMessage="NOT FINAL - One of the benefits of becoming a Full VIP is that each month when they are billed, they receive a Member Credit that can be redeemed for any item or outfit up to $100."
        />
      </TooltipMessage>
    </Tooltip>
  );
};

/*
 * Logic copied from src/shared/components/MemberCreditCallout/MemberCreditCallout.js
 */
const MemberCreditCallout = ({ product, isTokenOnly = false }) => {
  const { tokenValue } = useEMPPricing(product);
  const price = product.pricing.final;
  const { isByoQualifiedProduct, offerTypeKey, offerTypePlainText } =
    useByoConfigForProduct({
      productFplGlobalCodes: product?.activeFeaturedProductLocationCodeList,
      productFplIds: product?.activeFeaturedProductLocationIdList,
      masterProductId:
        product?.bundleItems?.length > 0 ? product.masterProductId : null,
    });

  const { inFreeTrial } = useMembership();
  const { tld } = useDomain();

  let callout = null;

  if (isByoQualifiedProduct) {
    // TODO(FLCE-3465): switch positional `{1}` placeholder to a named one
    // (e.g. `{offerType}`) and align bundle content with content team.
    callout = (
      <DynamicFormattedMessage
        id={`byob.${offerTypeKey}_member_credit_callout`}
        defaultMessage={`${offerTypePlainText} Member Credit`}
        values={{ 1: offerTypePlainText }}
      />
    );
  } else if (price >= getMemberCreditPriceThreshold(tld)) {
    callout = (
      <MemberCreditSection data-autotag={`${product.itemNumber}-membercredit1`}>
        {tokenValue === 1 ? (
          <FormattedMessage
            id="grid_static.outfit_credit"
            defaultMessage="1 VIP Member Credit"
          />
        ) : (
          <FormattedMessage
            id="grid_static.outfit_credits"
            defaultMessage="{tokenValue} VIP Member Credits"
            values={{ tokenValue }}
          />
        )}
        {inFreeTrial && <FreeTrialMemberCreditTooltip />}
      </MemberCreditSection>
    );
  }

  if (!callout) {
    return null;
  }

  if (isTokenOnly) {
    return <TokenOnlyText>{callout}</TokenOnlyText>;
  }

  return (
    <DividerAndCredit>
      <Divider />
      <SmallText>{callout}</SmallText>
    </DividerAndCredit>
  );
};

MemberCreditCallout.propTypes = {
  isTokenOnly: PropTypes.bool,
  product: PropTypes.shape({
    activeFeaturedProductLocationCodeList: PropTypes.arrayOf(PropTypes.string),
    activeFeaturedProductLocationIdList: PropTypes.arrayOf(PropTypes.number),
    bundleItems: PropTypes.array,
    itemNumber: PropTypes.string,
    pricing: PropTypes.object,
    retailUnitPrice: PropTypes.number,
    defaultUnitPrice: PropTypes.number,
    outletUnitPrice: PropTypes.number,
    promos: PropTypes.object,
    promoCode: PropTypes.string,
    masterProductId: PropTypes.number,
  }),
};

export default MemberCreditCallout;
